/**
 * @file ScoutDateInput atom — portal-safe date text input for mobile filter sheets.
 * @description Wraps the Gluestack UIInput + InputField with isDark-aware theme
 * styling via StyleSheet.create. Required because NativeWind `dark:` classes do
 * not resolve inside GlueStack Actionsheet portals on native iOS, so className-only
 * theming silently shows wrong colors in those contexts.
 * @module components/atoms/ScoutDateInput
 */

import React from "react";
import { StyleSheet } from "react-native";

import { Input as UIInput, InputField } from "@/components/ui/input";
import { RADIUS } from "@/config/radius";

/**
 *
 */
export interface ScoutDateInputProps {
  /** Whether dark mode is active — drives portal-safe background/border colors. */
  readonly isDark: boolean;
  /** Controlled text value. */
  readonly value: string;
  /** Called with the new value on every keystroke. */
  readonly onChangeText: (text: string) => void;
  /** Placeholder text shown when value is empty. */
  readonly placeholder?: string;
  /** Test identifier forwarded to the outer UIInput. */
  readonly testID?: string;
  /** Called when the field loses focus. */
  readonly onBlur?: () => void;
  /** Accessible name forwarded to the inner text field. */
  readonly accessibilityLabel?: string;
}

/**
 * Portal-safe themed date input.
 * Uses StyleSheet.create so isDark colors are applied without UNSAFE_style.
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.value - Controlled text value
 * @param props.onChangeText - Called with new value on every keystroke
 * @param props.placeholder - Placeholder text
 * @param props.testID - Test identifier
 * @param props.onBlur - Called when the field loses focus
 * @param props.accessibilityLabel - Accessible name for the text field
 */
const ScoutDateInput = ({
  isDark,
  value,
  onChangeText,
  placeholder = "YYYY-MM-DD",
  testID,
  onBlur,
  accessibilityLabel,
}: ScoutDateInputProps) => (
  <UIInput
    testID={testID}
    variant="outline"
    size="md"
    className="h-auto"
    style={[styles.base, isDark ? styles.dark : styles.light]}
  >
    <InputField
      placeholder={placeholder}
      value={value}
      onChangeText={onChangeText}
      onBlur={onBlur}
      // Gluestack's InputField overwrites accessibilityLabel with its own
      // aria-label default ("Input Field"), so the name goes in as aria-label.
      aria-label={accessibilityLabel}
      className="text-content-primary"
    />
  </UIInput>
);

const styles = StyleSheet.create({
  base: {
    borderRadius: RADIUS.sm,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderWidth: 1,
  },
  light: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D3D3D3",
  },
  dark: {
    backgroundColor: "#1C1C1E",
    borderColor: "#38383A",
  },
});

ScoutDateInput.displayName = "ScoutDateInput";

export { ScoutDateInput };
